// Community Management -> LIVE ACTIVITY (Family only; server gate:
// auth + requireFamily on every /family/* endpoint).
//
// One view = ONE stream carrying exactly the topics this view shows:
//   community                 always (Everyone / visitors / users / by role)
//   accounts + roleGrowth     only once Family starts a "Since" count
// Wall lists and fixed Wall statistics are plain requests, fetched only when
// the Wall section or a Wall scope is actually opened.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLiveStream } from "./useLiveStream";
import { getWallsAPI, getScopeStatsAPI } from "../../auth/liveApi";
import {
  ROLE_KEYS, ROLE_LABELS, LiveIndicator, LastUpdated, LiveNumber, SinceModal, formatCount, formatWhen,
} from "./LiveParts";
import { elakayAutoText, elakayPluralText } from "../../pageInside/dataElakay";
import { roleDisplayLabel } from "../../../utils/roleDisplayLabel";
import ActivityHistoryPanel from "./ActivityHistoryPanel";
import IndividualActivityTable from "./individual/IndividualActivityTable";

const STORAGE_KEY = "elakay_community_live";
const DEFAULTS = { scopeType: "elakay", wallIds: [], people: "everyone", role: "", since: null };

const readSaved = () => {
  try { return { ...DEFAULTS, ...JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "{}") }; } catch { return { ...DEFAULTS }; }
};

export const scopeParam = ({ scopeType, wallIds }) => {
  if (scopeType === "all_walls") return "all_walls";
  if (scopeType === "wall" && wallIds.length === 1) return `wall:${wallIds[0]}`;
  if (scopeType === "walls" && wallIds.length) return `walls:${wallIds.join(",")}`;
  return "elakay";
};

const WallPicker = ({ multiple, selected, onChange, t }) => {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [res, setRes] = useState({ loading: true, data: [], pagination: null, error: null });
  useEffect(() => {
    const controller = new AbortController();
    const id = setTimeout(() => {
      setRes((r) => ({ ...r, loading: true }));
      getWallsAPI({ q, page, limit: 10 }, { signal: controller.signal })
        .then((d) => setRes({ loading: false, data: d.data, pagination: d.pagination, error: null }))
        .catch((e) => { if (e.name !== "AbortError") setRes({ loading: false, data: [], pagination: null, error: e.message }); });
    }, 300);
    return () => { clearTimeout(id); controller.abort(); };
  }, [q, page]);
  const toggle = (id) => {
    if (!multiple) return onChange([id]);
    return onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id].slice(0, 25));
  };
  return (
    <div className="live-wallpicker">
      <label htmlFor="live-wall-search"><span>{t("searchWall", "Find a Wall (Associate / Administrator)")}</span>
        <input id="live-wall-search" type="search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder={t("searchWallPh", "Name or email…")} />
      </label>
      {res.error && <p className="live-error" role="alert">{res.error}</p>}
      <ul>
        {res.data.map((w) => (
          <li key={w.id}>
            <label>
              <input type={multiple ? "checkbox" : "radio"} name="live-wall" checked={selected.includes(w.id)} onChange={() => toggle(w.id)} />
              <span><strong>{w.name}</strong> <small>{roleDisplayLabel(w.role)} · {elakayPluralText(w.members, ["h_live_n_members_one", "{value0} member"], ["h_live_n_members", "{value0} members"], { value0: formatCount(w.members) })}</small></span>
            </label>
          </li>
        ))}
        {!res.loading && !res.data.length && <li className="live-muted">{t("noWalls", "No Wall found.")}</li>}
      </ul>
      {res.pagination && res.pagination.totalPages > 1 && (
        <div className="live-pager">
          <button type="button" className="live-btn live-btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>{t("previous", "Previous")}</button>
          <span>{page} / {res.pagination.totalPages}</span>
          <button type="button" className="live-btn live-btn-ghost" disabled={page >= res.pagination.totalPages} onClick={() => setPage(page + 1)}>{t("next", "Next")}</button>
        </div>
      )}
    </div>
  );
};

const WallStats = ({ scope, t }) => {
  const [res, setRes] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    const controller = new AbortController();
    setRes({ loading: true, data: null, error: null });
    getScopeStatsAPI({ scope }, { signal: controller.signal })
      .then((d) => setRes({ loading: false, data: d, error: null }))
      .catch((e) => { if (e.name !== "AbortError") setRes({ loading: false, data: null, error: e.message }); });
    return () => controller.abort();
  }, [scope]);
  if (res.error) return <p className="live-error" role="alert">{res.error}</p>;
  if (!res.data) return <p className="live-muted">{t("loading", "Loading…")}</p>;
  const d = res.data;
  return (
    <div className="live-wallstats">
      <p className="live-muted">{t("fixedStats", "Fixed statistics (not live)")} · {t("asOf", "as of")} {formatWhen(d.asOf)}</p>
      <div className="live-tiles">
        <div className="live-tile"><span>{t("members", "Members")}</span><strong>{formatCount(d.members)}</strong></div>
        <div className="live-tile"><span>{t("new30", "New members (30 days)")}</span><strong>{formatCount(d.newMembersLast30Days)}</strong></div>
        {d.scope.leaderCount != null && <div className="live-tile"><span>{t("wallsInScope", "Walls in scope")}</span><strong>{formatCount(d.scope.leaderCount)}</strong></div>}
      </div>
      <table className="live-table">
        <thead><tr><th>{t("role", "Role")}</th><th className="num">{t("members", "Members")}</th></tr></thead>
        <tbody>{ROLE_KEYS.map((r) => <tr key={r}><td>{ROLE_LABELS[r]}</td><td className="num">{formatCount(d.membersByRole?.[r])}</td></tr>)}</tbody>
      </table>
    </div>
  );
};

const CommunityLive = ({ streamImpl }) => {
  const t = useCallback((key, fallback, values) => elakayAutoText(`h_live_${key}`, fallback, values), []);
  const [f, setF] = useState(readSaved);
  const [sinceModal, setSinceModal] = useState(false);
  const [showWallStats, setShowWallStats] = useState(false);
  const [showDefs, setShowDefs] = useState(false);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
  useEffect(() => { try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(f)); } catch { /* optional */ } }, [f]);

  const scope = scopeParam(f);
  const wallScopePending = (f.scopeType === "wall" || f.scopeType === "walls") && !f.wallIds.length;
  const topics = f.since ? "community,accounts,roleGrowth" : "community";
  const params = useMemo(() => ({ topics, scope, ...(f.since ? { since: f.since } : {}) }), [topics, scope, f.since]);
  const live = useLiveStream({ enabled: !wallScopePending, params, ...(streamImpl ? { streamImpl } : {}) });
  const c = live.data.community;
  const acc = live.data.accounts;
  const growth = live.data.roleGrowth;
  const isElakay = scope === "elakay";

  const focus = (() => {
    if (!c) return { label: t("everyoneNow", "EVERYONE ACTIVE NOW"), value: null };
    if (f.role) return { label: t("activeNowRole", "Active now — {value0}", { value0: ROLE_LABELS[f.role] }), value: c.byRole?.[f.role] || 0 };
    if (f.people === "anonymous") return { label: t("anonNow", "ANONYMOUS VISITORS NOW"), value: c.anonymousVisitors };
    if (f.people === "authenticated") return { label: t("usersNow", "ACTIVE USERS NOW"), value: c.authenticatedUsers };
    return { label: t("everyoneNow", "EVERYONE ACTIVE NOW"), value: c.everyone };
  })();
  const roleSum = c ? ROLE_KEYS.reduce((s, r) => s + (c.byRole?.[r] || 0), 0) + (c.byRole?.unknown || 0) : 0;

  return (
    <div className="live-root">
      <header className="live-header">
        <div>
          <h2>{t("title", "Live Activity")}</h2>
          <p className="live-muted">{t("subtitle", "Who is on Elakay right now. Counts only — no individual tracking. Runs only while this view is open.")}</p>
        </div>
        <div className="live-actions">
          <LiveIndicator status={wallScopePending ? "off" : live.status} t={t} />
          <LastUpdated at={live.lastUpdated} t={t} />
          <button type="button" className="live-btn live-btn-ghost" onClick={() => setShowDefs((v) => !v)} aria-expanded={showDefs}>{t("definitions", "Definitions")}</button>
        </div>
      </header>

      {showDefs && (
        <aside className="live-defs">
          <p><strong>{t("defActive", "Active")}</strong>: {t("defActiveText", "a browser tab that is visible and was used (click, key, scroll, touch) in the last 5 minutes, and reported itself in the last")} {c?.windowSeconds || 150}s. {t("defNotLogged", "Being logged in is not enough.")}</p>
          <p><strong>{t("defAnon", "Anonymous visitor")}</strong>: {t("defAnonText", "one active browser without a valid login. Several tabs of one browser = one visitor.")}</p>
          <p><strong>{t("defUser", "Active user")}</strong>: {t("defUserText", "one account with at least one active browser — 5 tabs, a laptop and a phone still count once.")}</p>
          <p><strong>{t("defEveryone", "Everyone active now")}</strong> = {t("defEveryoneText", "anonymous visitors + active users. Each person is in exactly one category.")}</p>
          <p><strong>{t("defRole", "Role")}</strong>: {t("defRoleText", "the account's active role (userActiveRole). A role proposed but not accepted yet counts as User. The “view as role” switch is ignored.")}</p>
          <p><strong>{t("defTabs", "Open tabs / browsers")}</strong>: {t("defTabsText", "raw connections, shown separately — never used as a people count.")}</p>
          <p><strong>{t("defWall", "Wall")}</strong>: {t("defWallText", "an Associate's or Administrator's team: people whose sponsor chain (userAdmin / Admintean) includes them. Anonymous visitors belong to no Wall.")}</p>
        </aside>
      )}

      <section className="live-filterbar" aria-label={t("filters", "Filters")}>
        <label htmlFor="live-scope"><span>{t("scope", "Scope")}</span>
          <select id="live-scope" value={f.scopeType} onChange={(e) => set({ scopeType: e.target.value, wallIds: [] })}>
            <option value="elakay">{t("scopeElakay", "Entire Elakay")}</option>
            <option value="all_walls">{t("scopeAllWalls", "All Walls")}</option>
            <option value="wall">{t("scopeOneWall", "One Wall")}</option>
            <option value="walls">{t("scopeSelectedWalls", "Selected Walls")}</option>
          </select>
        </label>
        <label htmlFor="live-people"><span>{t("people", "People")}</span>
          <select id="live-people" value={f.people} onChange={(e) => set({ people: e.target.value, role: "" })}>
            <option value="everyone">{t("everyone", "Everyone")}</option>
            <option value="anonymous" disabled={!isElakay}>{t("anonymous", "Anonymous visitors")}</option>
            <option value="authenticated">{t("authenticated", "Authenticated users")}</option>
          </select>
        </label>
        <label htmlFor="live-role"><span>{t("role", "Role")}</span>
          <select id="live-role" value={f.role} onChange={(e) => set({ role: e.target.value, people: e.target.value ? "authenticated" : f.people })}>
            <option value="">{t("allRoles", "All roles")}</option>
            {ROLE_KEYS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </label>
        <label htmlFor="live-mode"><span>{t("mode", "Mode")}</span>
          <select id="live-mode" value={f.since ? "since" : "now"} onChange={(e) => (e.target.value === "since" ? setSinceModal(true) : set({ since: null }))}>
            <option value="now">{t("modeNow", "Current / Now")}</option>
            <option value="since">{t("modeSince", "Since date/time")}</option>
          </select>
        </label>
        <button type="button" className="live-btn live-btn-ghost" onClick={() => setF({ ...DEFAULTS })}>{t("reset", "Reset filters")}</button>
      </section>

      {(f.scopeType === "wall" || f.scopeType === "walls") && (
        <WallPicker multiple={f.scopeType === "walls"} selected={f.wallIds} onChange={(wallIds) => set({ wallIds })} t={t} />
      )}
      {wallScopePending && <p className="live-muted">{t("pickWall", "Pick a Wall to start the live view.")}</p>}
      {live.error && <p className="live-error" role="alert">{live.error}</p>}

      {!wallScopePending && (
        <>
          <section className="live-card live-hero-card">
            <span className="live-hero-label">{focus.label}</span>
            <LiveNumber value={focus.value} className="live-number-xl" />
            {c?.scope?.leaders?.length > 0 && <span className="live-muted">{c.scope.leaders.map((l) => `${l.name} (${roleDisplayLabel(l.role)})`).join(", ")}</span>}
            {c && scope === "all_walls" && <span className="live-muted">{elakayPluralText(c.scope.leaderCount, ["h_live_n_walls_one", "{value0} Wall"], ["h_live_n_walls", "{value0} Walls"], { value0: formatCount(c.scope.leaderCount) })}</span>}
            {(f.people !== "everyone" || f.role) && (
              <button type="button" className="live-link" onClick={() => set({ people: "everyone", role: "" })}>{t("backEveryone", "← Everyone")}</button>
            )}
          </section>

          <div className="live-tiles">
            <button type="button" className={`live-tile ${f.people === "anonymous" ? "is-selected" : ""}`} disabled={!isElakay} onClick={() => set({ people: "anonymous", role: "" })}>
              <span>{t("anonymous", "Anonymous visitors")}</span><strong>{isElakay ? formatCount(c?.anonymousVisitors) : "—"}</strong>
              {!isElakay && <small>{t("anonNoWall", "Not part of any Wall")}</small>}
            </button>
            <button type="button" className={`live-tile ${f.people === "authenticated" && !f.role ? "is-selected" : ""}`} onClick={() => set({ people: "authenticated", role: "" })}>
              <span>{t("activeUsersAuth", "Active users (authenticated)")}</span><strong>{formatCount(c?.authenticatedUsers)}</strong>
            </button>
            <div className="live-tile"><span>{t("browsers", "Active browsers")}</span><strong>{formatCount(c?.devices)}</strong><small>{formatCount(c?.authenticatedDevices)} {t("loggedIn", "logged in")}</small></div>
            <div className="live-tile"><span>{t("tabs", "Open tabs (connections)")}</span><strong>{formatCount(c?.openTabs)}</strong><small>{t("notPeople", "not a people count")}</small></div>
          </div>

          <section className="live-card">
            <header className="live-card-head"><h3>{t("byRole", "ACTIVE BY ROLE")}</h3><span className="live-muted">{t("byRoleHint", "Each person counted once, under their active role. Click a row to focus it.")}</span></header>
            <table className="live-table live-breakdown">
              <tbody>
                {isElakay && (
                  <tr className={f.people === "anonymous" ? "is-selected" : ""}>
                    <td><button type="button" className="live-link" onClick={() => set({ people: "anonymous", role: "" })}>{t("anonymous", "Anonymous visitors")}</button> <small className="live-muted">{t("notARole", "(not a role)")}</small></td>
                    <td className="num">{formatCount(c?.anonymousVisitors)}</td>
                  </tr>
                )}
                {ROLE_KEYS.map((r) => (
                  <tr key={r} className={f.role === r ? "is-selected" : ""}>
                    <td><button type="button" className="live-link" onClick={() => set({ role: r, people: "authenticated" })}>{ROLE_LABELS[r]}</button></td>
                    <td className="num">{formatCount(c?.byRole?.[r])}</td>
                  </tr>
                ))}
                {c?.byRole?.unknown > 0 && <tr><td>{ROLE_LABELS.unknown}</td><td className="num">{formatCount(c.byRole.unknown)}</td></tr>}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row"><button type="button" className="live-link" onClick={() => set({ people: "everyone", role: "" })}>{t("total", "TOTAL — Everyone")}</button></th>
                  <td className="num"><strong>{formatCount(c?.everyone)}</strong></td>
                </tr>
                {c && (isElakay ? c.anonymousVisitors : 0) + roleSum !== c.everyone && (
                  <tr><td colSpan={2} className="live-error">{t("mismatch", "Breakdown does not add up — please refresh.")}</td></tr>
                )}
              </tfoot>
            </table>
          </section>

          <section className="live-card">
            <header className="live-card-head">
              <h3>{t("newAccounts", "NEW ACCOUNTS & ROLE GROWTH")}</h3>
              {f.since ? (
                <div className="live-actions">
                  <span className="live-muted">{formatWhen(f.since)} → {t("now", "now")}</span>
                  <button type="button" className="live-btn live-btn-ghost" onClick={() => setSinceModal(true)}>{t("changeStart", "Change start")}</button>
                  <button type="button" className="live-btn live-btn-ghost" onClick={() => set({ since: null })}>{t("stop", "Stop")}</button>
                </div>
              ) : (
                <button type="button" className="live-btn" onClick={() => setSinceModal(true)}>● {t("startSince", "Count since…")}</button>
              )}
            </header>
            {!f.since && <p className="live-muted">{t("sinceOff", "Off. Cumulative counts (they only go up) from a start date/time you choose.")}</p>}
            {f.since && (
              <>
                <div className="live-tiles">
                  <div className="live-tile"><span>{t("newAccountsLabel", "New accounts (activated)")}</span><LiveNumber value={acc ? acc.newAccounts : null} /><small>{formatCount(acc?.pendingActivation)} {t("awaiting", "awaiting activation")}</small></div>
                  <div className="live-tile"><span>{t("rolesGranted", "Roles obtained")}</span><LiveNumber value={growth ? growth.total : null} /></div>
                </div>
                <table className="live-table">
                  <thead><tr><th>{t("newRole", "New role holders")}</th><th className="num">{t("count", "Count")}</th></tr></thead>
                  <tbody>{ROLE_KEYS.filter((r) => r !== "user").map((r) => <tr key={r}><td>{t("newRoleHolder", "New {value0}", { value0: ROLE_LABELS[r] })}</td><td className="num">{formatCount(growth?.byRole?.[r])}</td></tr>)}</tbody>
                </table>
                <p className="live-muted">{t("growthRule", "A role counts when the workflow grants it (accepted role), not when it is only proposed. Accounts that obtained a role since the start and still hold it.")}</p>
              </>
            )}
          </section>

          <section className="live-card">
            <header className="live-card-head">
              <h3>{t("wallActivity", "WALL ACTIVITY")}</h3>
              <button type="button" className="live-btn live-btn-ghost" onClick={() => setShowWallStats((v) => !v)} aria-expanded={showWallStats}>
                {showWallStats ? t("hide", "Hide") : t("showWallStats", "Show Wall statistics")}
              </button>
            </header>
            {showWallStats && <WallStats scope={scope} t={t} />}
          </section>
        </>
      )}

      {/* Daily memory of the whole platform (not scoped to Walls), separate
          from the "active now" figures above. */}
      <ActivityHistoryPanel t={t} />

      {/* One row per identifiable browser (opt-in recording, Family only).
          Separate from the counts above; loads nothing until opened. */}
      <IndividualActivityTable />

      <SinceModal
        show={sinceModal}
        title={t("sinceModal", "New accounts & role growth — start")}
        initial={f.since}
        onHide={() => setSinceModal(false)}
        onStart={(at) => { setSinceModal(false); set({ since: at.toISOString() }); }}
        t={t}
      />
    </div>
  );
};

export default CommunityLive;
